import { FC } from 'react'
import classNames from 'classnames'

import { Tooltip } from '../../../../ui/Popups/Tooltip/Tooltip'
import { Truncate } from '../../../../ui/Truncate/Truncate'
import { Text, TextSmall } from '../../../../ui/Typography/Typography'
import {
  Color,
  PROGRESS_BAR_DURATION,
  ProgressBar,
} from '../../../../ui/v2/ProgressBar/ProgressBar'

import styles from './ProgressBarsBlock.module.scss'

export interface ProgressBarsSectionItem {
  percent: number | null
  /**
   * Set to true if there is just one main progress bar.
   * If has multiple progress bar items, then find dynamically active progress bar and set it to true, rest set to false.
   */
  isCurrent: boolean
  label?: string
  tooltip?: string
}

export interface ProgressBarsBlockItem {
  title: string | React.ReactNode
  label?: string | React.ReactNode
  isExtraInfoItem?: boolean
  tooltip?: string
  progressBarTooltip?: string
  sections: ProgressBarsSectionItem[]
}

interface ProgressBarsBlockProps {
  items: ProgressBarsBlockItem[]
  prize?: string | null
  className?: string
  color?: Color
}

export const ProgressBarsBlock: FC<ProgressBarsBlockProps> = (props) => {
  const { items, className, prize, color = 'red' } = props

  return (
    <div className={classNames(styles.progressBarsBlock, className)}>
      {items.map((item, index) => {
        return (
          <ProgressBarBlock
            key={index}
            color={color}
            {...item}
            renderExtraInfo={() =>
              item.isExtraInfoItem && prize ? (
                <section className={styles.progressBarPrizeInfo}>
                  <Text>{prize}</Text>
                </section>
              ) : (
                <></>
              )
            }
          />
        )
      })}
    </div>
  )
}

interface ProgressBarBlockProps extends ProgressBarsBlockItem {
  color: Color
  renderExtraInfo?: () => React.ReactNode
}

export const ProgressBarBlock: FC<ProgressBarBlockProps> = (props) => {
  const { title, label, sections, renderExtraInfo, color, tooltip, progressBarTooltip } = props

  return (
    <div className={styles.progressBarBlock}>
      <div className={styles.progressBarBlockHead}>
        <div className={styles.progressBarBlockTitle}>
          <Tooltip.Menu direction='topRight' disabled={!tooltip?.length} content={tooltip}>
            <Text>{title}</Text>
          </Tooltip.Menu>
        </div>
        <TextSmall>{label}</TextSmall>
      </div>
      <div className={styles.progressBarBlockProgress}>
        {sections.map((section, index) => (
          <ProgressBarSection
            key={index}
            {...section}
            delay={index * PROGRESS_BAR_DURATION * 0.9}
            color={color}
            tooltip={progressBarTooltip}
          />
        ))}
      </div>
      {renderExtraInfo && renderExtraInfo()}
    </div>
  )
}

export interface ProgressBarsSectionItem {
  percent: number | null
  /**
   * Set to true if there is just one main progress bar.
   * If has multiple progress bar items, then find dynamically active progress bar and set it to true, rest set to false.
   */
  isCurrent: boolean
  label?: string
  isNextStarted?: boolean
  tooltip?: string
}

interface ProgressBarSectionProps extends ProgressBarsSectionItem {
  color: Color
  delay?: number
}

const ProgressBarSection: FC<ProgressBarSectionProps> = (props) => {
  const { percent, label, color, isCurrent = false, isNextStarted, tooltip, delay } = props

  const shouldShowTooltip =
    (isCurrent && percent !== null && percent >= 0) || (!isCurrent && !isNextStarted)
  const tooltipContent = shouldShowTooltip && tooltip ? tooltip : null

  return (
    <div className={styles.progressBarSection}>
      <ProgressBar
        color={color}
        percent={percent}
        tooltip={tooltipContent}
        delay={delay}
        isCurrent={isCurrent}
      />
      {!!label?.length && (
        <div className={styles.progressBarLabelWrapper}>
          <Truncate>
            <TextSmall className={styles.progressBarLabel}>{label}</TextSmall>
          </Truncate>
        </div>
      )}
    </div>
  )
}
